import { Alert, Box, Button, Paper, Stack, Typography, Chip, CircularProgress, LinearProgress } from "@mui/material";
import { MdDescription, MdCheckCircle, MdOutlineImage } from "react-icons/md";
import MobileFooterLayout from "../../layout/MobileFooterLayout";
import { typographyStyles } from "constants/styles";
import type { IIncomeEntry } from "types";
import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { FILE_IMAGE_CONTENT_TYPES } from "constants/index";
import useFileUpload from "hooks/useFileUpload";

interface IncomeEntryConfirmationProps {
  incomeEntry: IIncomeEntry;
  loading: boolean;
  nextStep: (i: IIncomeEntry) => Promise<void>;
}

export default function IncomeEntryConfirmation({
  incomeEntry,
  loading,
  nextStep,
}: IncomeEntryConfirmationProps) {
  const [photo, setPhoto] = useState<{ fileId: number; originalName: string } | null>(
    incomeEntry.photo_file_id ? { fileId: incomeEntry.photo_file_id, originalName: "Foto previamente guardada" } : null,
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const operationInFlightRef = useRef(false);
  const { upload, status, progress, error } = useFileUpload();
  const isUploading = status === "requesting" || status === "uploading" || status === "confirming";
  const isBusy = loading || isUploading;
  const photoError = localError || error;

  const handleUploadPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || isBusy || operationInFlightRef.current) return;
    if (!FILE_IMAGE_CONTENT_TYPES.some((type) => type === file.type)) {
      setLocalError("Solo se permiten fotos JPEG o PNG. HEIC y otros formatos no estan soportados.");
      return;
    }

    operationInFlightRef.current = true;
    setLocalError(null);
    try {
      const confirmed = await upload(file);
      if (confirmed) setPhoto({ fileId: confirmed.fileId, originalName: confirmed.originalName });
    } finally {
      operationInFlightRef.current = false;
    }
  };

  const handleConfirm = async () => {
    if (!photo || isBusy || operationInFlightRef.current) return;
    operationInFlightRef.current = true;
    setLocalError(null);
    try {
      await nextStep({ ...incomeEntry, photo_file_id: photo.fileId });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : "No se pudo confirmar el preingreso.");
    } finally {
      operationInFlightRef.current = false;
    }
  };

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={typographyStyles.monospace}
        >
          Remito {incomeEntry?.numero_remito}
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
          {incomeEntry?.client?.razon_social}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transporte: {incomeEntry?.associated_client?.razon_social || "No especificado"}
        </Typography>

        <Chip
          icon={<MdOutlineImage />}
          label="Foto del remito"
          color="secondary"
          variant="outlined"
          sx={{ mt: 1.5 }}
        />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.75, lineHeight: 1.5 }}
        >
          Sacá foto al remito firmado para que quede constancia de la recepción.
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1.75,
          borderStyle: "dashed",
          borderColor: photo ? "success.main" : "warning.main",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: 1,
              borderColor: "divider",
              color: "text.secondary",
            }}
          >
            <MdDescription size={20} />
          </Box>
          <Box
            sx={{ flex: 1, gap: 0.5, display: "flex", flexDirection: "column" }}
          >
            <Typography variant="body2" fontWeight={700}>
              Foto del remito firmado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Obligatoria para confirmar · se envía al cliente (JPEG o PNG, hasta 10 MiB. )
            </Typography>
            {photo && (
              <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
                {photo.originalName}
              </Typography>
            )}
            <Button
              component="label"
              size="small"
              variant="outlined"
              color={photo ? "success" : "warning"}
              disabled={isBusy}
              sx={{ mt: 0.5, alignSelf: "center" }}
            >
              {photo ? "Cambiar foto" : "Fotografiar"}
              <input
                type="file"
                accept={FILE_IMAGE_CONTENT_TYPES.join(",")}
                aria-label="Seleccionar foto del remito"
                capture="environment"
                hidden
                disabled={isBusy}
                onChange={handleUploadPhoto}
              />
            </Button>
          </Box>
        </Stack>
        {isUploading && (
          <Box role="status" aria-live="polite" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {status === "requesting" ? "Solicitando permiso de carga..." :
                status === "confirming" ? "Verificando foto..." : `Subiendo foto: ${progress}%`}
            </Typography>
            <LinearProgress
              aria-label="Progreso de carga de foto"
              variant={status === "uploading" ? "determinate" : "indeterminate"}
              value={progress}
            />
          </Box>
        )}
        {photoError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {photoError}{photo && " La foto confirmada se conserva."}
          </Alert>
        )}
      </Paper>
      <MobileFooterLayout>
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          aria-label="Confirmar ingreso"
          disabled={isBusy || !photo}
          onClick={handleConfirm}
          sx={{ py: 1.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 1  }}
        >
          {loading ? <CircularProgress color="success" size={24}/> : (<>
            Confirmar ingreso <MdCheckCircle size={20}/>
          </>)}
        </Button>
      </MobileFooterLayout>
    </>
  );
}
